import { Box, Text } from "@dehub/theme";
import { RowAction } from "@/shared/a11y/row-action";
import { MissingPermission } from "@/shared/a11y/trans-code";
import type { DataTableColumn } from "@dehub/theme";
import { FileTextIcon, PencilSimpleIcon, PowerIcon, UsersIcon } from "@phosphor-icons/react";
import type { TFunction } from "i18next";
import type { ReactNode } from "react";
import type { IBusinessListRow } from "@/models/Business";
import type { IReference } from "@/models/Reference";
import { formatDateTime } from "@/utils/format-date";
import { renderBusinessName } from "@/utils/render-business-name";
import type { BusinessPermissions } from "../permissions";
import type { BusinessType, RowActionKey } from "../types";
import { BusinessLogo } from "./BusinessLogo";
import classes from "../business.module.css";

type Args = {
  type: BusinessType;
  perms: BusinessPermissions;
  businessStatus: IReference[];
  t: TFunction;
  tCommon: TFunction;
  /** Set while the partner has no address: the backend refuses every write until it does. */
  addressReason: ReactNode;
  onRowAction: (key: RowActionKey, row: IBusinessListRow) => void;
  onLogoPreview: (src: string, name: string) => void;
};

export function createBusinessColumns({
  perms,
  businessStatus,
  addressReason,
  t,
  tCommon,
  onRowAction,
  onLogoPreview,
}: Args): DataTableColumn<IBusinessListRow>[] {
  const missing = (code: string) => <MissingPermission code={code} />;

  const statusLabel = (code?: string | null) => {
    const reference = businessStatus.find((r) => r?.code === code);
    if (code) {
      const key = `status.${code}`;
      const translated = tCommon(key);
      if (translated !== key) return translated;
    }
    return reference?.name ?? code ?? "-";
  };

  return [
    {
      key: "actions",
      header: tCommon("col.action"),
      width: 150,
      render: (row) => {
        const active = row.businessStatus === "ACTIVE";
        // Edit opens the drawer, which loads GET /prt/business/:id (needs DETAIL) and saves with PUT (needs EDIT).
        const editMissing = [
          !perms.businessEdit.hasAccess ? perms.codes.businessEdit : null,
          !perms.businessDetail.hasAccess ? perms.codes.businessDetail : null,
        ]
          .filter(Boolean)
          .join(", ");
        const editReason = editMissing
          ? missing(editMissing)
          : addressReason ?? (row.isConfirmed ? t("action.editConfirmed") : null);
        // The backend refuses any status change of a confirmed business, in both directions.
        const toggleReason = !perms.changeStatus.hasAccess
          ? missing(perms.codes.changeStatus)
          : addressReason ?? (row.isConfirmed ? t("action.statusConfirmed") : null);
        return (
          <Box className={classes.rowActions}>
            <RowAction
              label={tCommon("actions.edit")}
              reason={editReason}
              icon={PencilSimpleIcon}
              anchor={`${row.id}:edit`}
              onClick={() => onRowAction("edit", row)}
            />
            <RowAction
              label={tCommon("actions.detail")}
              reason={perms.businessDetail.hasAccess ? null : missing(perms.codes.businessDetail)}
              icon={FileTextIcon}
              anchor={`${row.id}:detail`}
              onClick={() => onRowAction("detail", row)}
            />
            <RowAction
              label={tCommon("actions.users")}
              reason={perms.userList.hasAccess ? null : missing(perms.codes.userList)}
              icon={UsersIcon}
              anchor={`${row.id}:users`}
              onClick={() => onRowAction("users", row)}
            />
            <RowAction
              label={active ? tCommon("actions.deactivate") : tCommon("actions.activate")}
              reason={toggleReason}
              icon={PowerIcon}
              // Red = turns the business off, green = turns it on.
              color={active ? "red" : "green"}
              anchor={`${row.id}:toggle`}
              onClick={() => onRowAction(active ? "deactivate" : "activate", row)}
            />
          </Box>
        );
      },
    },
    {
      key: "business",
      header: t("col.business"),
      render: (row) => (
        <Box className={classes.bizCell}>
          <BusinessLogo logo={row.logo} name={row.profileName} nameEng={row.profileNameEng} onPreview={onLogoPreview} />
          <Box className={classes.bizText}>
            <Text component="span" className={classes.bizName}>
              {renderBusinessName({ profileName: row.profileName, profileNameEng: row.profileNameEng })}
            </Text>
            <Text component="span" className={classes.mono} translate="no">
              {row.refCode ?? "-"}
            </Text>
          </Box>
        </Box>
      ),
    },
    {
      key: "partner",
      header: tCommon("col.partner"),
      render: (row) => (
        <Text component="span" className={classes.muted}>
          {renderBusinessName({ partnerName: row.partnerName, partnerNameEng: row.partnerNameEng })}
        </Text>
      ),
    },
    {
      key: "isDefault",
      header: tCommon("col.default"),
      width: 110,
      render: (row) => (
        <Text component="span" className={classes.muted}>
          {row.isDefault ? t("main.yes") : t("main.no")}
        </Text>
      ),
    },
    {
      key: "status",
      header: tCommon("col.status"),
      width: 210,
      render: (row) => (
        <Box className={classes.pills}>
          <Box component="span" className={`${classes.pill} ${row.businessStatus === "ACTIVE" ? classes.pillActive : ""}`}>
            <Box component="span" className={classes.pillDot} />
            {statusLabel(row.businessStatus)}
          </Box>
          {row.isConfirmed ? (
            <Box component="span" className={classes.pill}>
              {t("confirmed")}
            </Box>
          ) : null}
        </Box>
      ),
    },
    {
      key: "createdAt",
      header: t("col.created"),
      width: 160,
      render: (row) => (
        <Text component="span" className={classes.mono}>
          {formatDateTime(row.createdAt) || "-"}
        </Text>
      ),
    },
  ];
}
